import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../../u2000Core/axios/U2000UseAxiosAuthenticated';
import { U2000HandleApiError } from '../../u2000Core/axios/U2000HandleApiError';
import { useToast } from '../../u2000Core/context/toast/U2000ToastContext';
import { NS } from '../U3203';
import {
  doGetRevision,
  doListRevisions,
  doRestoreRevision,
  luHandleAppiError,
  normalizeU3203AiMissionFromApi,
  type U3203AiMissionFormRow,
  type U3203AiMissionRevision,
  type U3203AiMissionRevisionSummary,
} from '../U3203Service';

export function useU3203AiMissionRevisionHandlers() {
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const [revisions, setRevisions] = useState<U3203AiMissionRevisionSummary[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingRevision, setLoadingRevision] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const revisionCacheRef = useRef<Map<number, U3203AiMissionRevision>>(new Map());

  const handleApiError = useCallback(
    (error: unknown) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
    [t, toast],
  );

  const loadRevisions = useCallback(
    async (missionId: string): Promise<U3203AiMissionRevisionSummary[]> => {
      setLoadingList(true);
      try {
        const response = await doListRevisions(u2000AxiosAuthenticated, missionId);
        const list = response.data.results ?? [];
        setRevisions(list);
        revisionCacheRef.current.clear();
        return list;
      } catch (error: unknown) {
        handleApiError(error);
        setRevisions([]);
        return [];
      } finally {
        setLoadingList(false);
      }
    },
    [u2000AxiosAuthenticated, handleApiError],
  );

  const loadRevision = useCallback(
    async (missionId: string, revisionNumber: number): Promise<U3203AiMissionRevision | null> => {
      const cached = revisionCacheRef.current.get(revisionNumber);
      if (cached) {
        return cached;
      }
      setLoadingRevision(true);
      try {
        const response = await doGetRevision(u2000AxiosAuthenticated, missionId, revisionNumber);
        const revision = response.data.result;
        revisionCacheRef.current.set(revisionNumber, revision);
        return revision;
      } catch (error: unknown) {
        handleApiError(error);
        return null;
      } finally {
        setLoadingRevision(false);
      }
    },
    [u2000AxiosAuthenticated, handleApiError],
  );

  const restoreRevision = useCallback(
    async (
      missionId: string,
      revisionNumber: number,
      version: number,
      comment?: string | null,
    ): Promise<U3203AiMissionFormRow | null> => {
      setRestoring(true);
      try {
        const response = await doRestoreRevision(u2000AxiosAuthenticated, missionId, revisionNumber, {
          version,
          comment: comment?.trim() ? comment.trim() : null,
        });
        const normalized = normalizeU3203AiMissionFromApi(response.data.result);
        revisionCacheRef.current.clear();
        await loadRevisions(missionId);
        return normalized;
      } catch (error: unknown) {
        handleApiError(error);
        return null;
      } finally {
        setRestoring(false);
      }
    },
    [u2000AxiosAuthenticated, handleApiError, loadRevisions],
  );

  return {
    revisions,
    loadingList,
    loadingRevision,
    restoring,
    loadRevisions,
    loadRevision,
    restoreRevision,
  };
}
